import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { doc, getDoc, setDoc, updateDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { QRCodeSVG } from "qrcode.react";
import { db } from "./firebaseConfig";
import Navbar from "./components/navbar";
import { Helmet } from "@dr.pogodin/react-helmet";
import Footer from "./components/footer";
import { slideImageMode, sectionHasImage } from "./sectionLayout";
import "./css/slideshow.css";
// Short, easy-to-read codes for the remote session doc id — excludes
// look-alike characters (0/O, 1/I/L) in case someone has to type it in.
const SESSION_CODE_CHARS = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
function generateSessionCode() {
    let code = "";
    for (let i = 0; i < 6; i += 1) {
        code += SESSION_CODE_CHARS[Math.floor(Math.random() * SESSION_CODE_CHARS.length)];
    }
    return code;
}
const WATERMARK_SRC = "/SOLE-logo.svg";
const CURRENT_YEAR = new Date().getFullYear();
// ---- HTML normalization utilities (match Talk page behavior) ----
function mergeAndNormalizeHTML(htmlParts) {
    const container = document.createElement('div');

    // Append all parts
    htmlParts.forEach((html) => {
        if (!html) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        while (tmp.firstChild) container.appendChild(tmp.firstChild);
    });

    // Preserve empty paragraphs (<p><br></p>) as visual gaps that we can style via CSS
    Array.from(container.querySelectorAll('p')).forEach((p) => {
        const onlyBr = p.children.length === 1 && p.firstElementChild?.tagName === 'BR';
        const isEmpty = p.textContent.trim() === '';
        if (onlyBr || isEmpty) {
            const gap = document.createElement('div');
            gap.className = 'section-gap';
            gap.setAttribute('data-lines', '1');
            p.replaceWith(gap);
        }
    });

    // Merge consecutive gap nodes
    (function mergeGaps(root) {
        let node = root.firstChild;
        while (node) {
            if (node.nodeType === 1 && node.classList.contains('section-gap')) {
                let lines = 1;
                let next = node.nextSibling;
                while (next && next.nodeType === 1 && next.classList?.contains('section-gap')) {
                    lines += parseInt(next.getAttribute('data-lines') || '1', 10);
                    const toRemove = next;
                    next = next.nextSibling;
                    toRemove.remove();
                }
                node.setAttribute('data-lines', String(lines));
                node.style.display = 'block';
                node.style.height = (lines * 1.09375) + 'cqw';
            }
            node = node.nextSibling;
        }
    })(container);

    // List reconstruction (Quill emits per-li data attributes)
    function buildList(tag) { return document.createElement(tag); }
    function liLevel(li) {
        const m = (li.className || '').match(/ql-indent-(\d+)/);
        return m ? parseInt(m[1], 10) : 0;
    }
    function liType(li, parentTag) {
        const t = li.getAttribute('data-ql-list') || li.getAttribute('data-list');
        if (t === 'bullet') return 'ul';
        if (t === 'ordered') return 'ol';
        return parentTag.toLowerCase();
    }
    function cleanLi(li) {
        const newLi = document.createElement('li');
        Array.from(li.childNodes).forEach((child) => {
            if (child.nodeType === 1 && child.classList.contains('ql-ui')) return;
            newLi.appendChild(child.cloneNode(true));
        });
        newLi.removeAttribute('data-ql-list');
        newLi.removeAttribute('data-list');
        newLi.className = (li.className || '').replace(/\bql-indent-\d+\b/g, '').trim();
        return newLi;
    }
    function reconstructList(listNode) {
        const parentTag = listNode.tagName.toLowerCase();
        const lis = Array.from(listNode.querySelectorAll(':scope > li'));
        const frag = document.createDocumentFragment();
        const stack = [{ node: frag, level: -1, type: null }];
        lis.forEach((li) => {
            const level = liLevel(li);
            const type = liType(li, parentTag);
            while (stack[stack.length - 1].level >= level) stack.pop();
            let top = stack[stack.length - 1];
            let currentList = top.node.lastElementChild;
            if (!currentList || currentList.tagName.toLowerCase() !== type) {
                currentList = buildList(type);
                if (listNode.className) currentList.className = listNode.className;
                // Preserve ordered-list start value from the original list when at the root level
                if (parentTag === 'ol' && listNode.hasAttribute('start') && stack.length === 1 && type === 'ol') {
                    currentList.setAttribute('start', listNode.getAttribute('start'));
                }
                top.node.appendChild(currentList);
            }
            stack.push({ node: currentList, level, type });
            currentList.appendChild(cleanLi(li));
        });
        listNode.replaceWith(frag);
    }
    Array.from(container.querySelectorAll('ol, ul')).forEach((list) => reconstructList(list));

    // Merge adjacent lists of the same type so they appear seamless (only within a single slide)
    let node = container.firstChild;
    while (node) {
        const next = node.nextSibling;
        if (
            next &&
            node.nodeType === 1 && next.nodeType === 1 &&
            (node.tagName === 'UL' || node.tagName === 'OL') &&
            next.tagName === node.tagName
        ) {
            while (next.firstChild) node.appendChild(next.firstChild);
            next.remove();
            continue;
        }
        node = next;
    }

    return container.innerHTML;
}
// --- Helpers to continue list numbering across reference parts
function countListItems(html) {
    try {
        const div = document.createElement('div');
        div.innerHTML = html || '';
        const ol = div.querySelector('ol');
        if (!ol) return 0;

        const allLis = Array.from(ol.querySelectorAll('li'));
        if (allLis.length === 0) return 0;

        // If Quill indent classes are present, only count top-level items (indent 0)
        const hasQuillIndent = allLis.some(li => /ql-indent-\d+/.test(li.className || ''));
        if (hasQuillIndent) {
            return allLis.filter(li => {
                const m = (li.className || '').match(/ql-indent-(\d+)/);
                return !m || parseInt(m[1], 10) === 0;
            }).length;
        }

        // Fallback: count only direct children of the first <ol> (normalized HTML case)
        return Array.from(ol.children).filter(el => el.tagName === 'LI').length;
    } catch (e) {
        return 0;
    }
}
function setOlStart(html, start) {
    if (!html || start <= 1) return html || '';
    try {
        const div = document.createElement('div');
        div.innerHTML = html;
        const ol = div.querySelector('ol');
        if (ol) ol.setAttribute('start', String(start));
        return div.innerHTML;
    } catch (e) {
        return html || '';
    }
}
// Automatically split sections into multiple slides at manual page breaks
export function paginateSections(sections) {
    const result = [];

    sections.forEach((sec) => {
        if (sec?.type === 'divider') {
            result.push({ header: sec.header || '', body: '', type: 'divider' });
            return;
        }

        const header = sec?.header || '';
        const hasImage = sectionHasImage(sec);
        const imgMode = hasImage ? slideImageMode(sec) : null; // 'both' | 'text' | 'image'
        const imageFields = {
            imageUrl: sec?.imageUrl || '',
            imageAlt: sec?.imageAlt || '',
            imageCaption: sec?.imageCaption || '',
        };

        // IMAGE ONLY — a single slide, no text pagination.
        if (hasImage && imgMode === 'image') {
            result.push({ header, body: '', type: sec?.type || 'content', ...imageFields });
            return;
        }

        // The image only rides along on the slides when the author asked for it.
        const carryImage = hasImage && imgMode === 'both' ? imageFields : {};

        // Which text to show on the slide(s): custom slide text if written, else the talk text.
        const usingSlideBody = typeof sec?.slideBody === 'string' && sec.slideBody.trim() !== '';
        const parts = [];
        if (usingSlideBody) {
            parts.push(sec.slideBody);
            if (Array.isArray(sec?.slideBodies)) sec.slideBodies.forEach((p) => { if (p) parts.push(p); });
        } else {
            if (sec?.body) parts.push(sec.body);
            if (Array.isArray(sec?.bodies)) sec.bodies.forEach((p) => { if (p) parts.push(p); });
        }

        if (parts.length === 0) {
            result.push({ header, body: '', type: sec?.type || 'content', ...carryImage });
            return;
        }

        let accumulated = 0;
        parts.forEach((html, idx) => {
            const startNum = accumulated + 1;
            const adjusted = setOlStart(html, startNum);
            const normalized = mergeAndNormalizeHTML([adjusted]);
            accumulated += countListItems(html);
            result.push({
                header: idx === 0 ? header : `${header} (Continued)`,
                body: normalized,
                type: sec?.type || 'content',
                ...carryImage,
            });
        });
    });

    return result;
}


function Watermark() {
    return (
        <img
            className="slide-watermark"
            src={WATERMARK_SRC}
            alt=""
            aria-hidden="true"
            draggable={false}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
    );
}

function Copyright() {
    return <div className="slide-copyright">© {CURRENT_YEAR} SOLE Ministries</div>;
}

// Build the full ordered slide list (title + content + questions + references)
// from a talk document. Shared by the presenter view and the phone remote so
// both always agree on what slide N actually is.
export function buildSlides(talk) {
    const { sections, title, verseText, verseRef } = talk || { sections: [], title: "", verseText: "", verseRef: "" };
    const paginated = paginateSections(sections);
    const slides = [
        { header: title, body: "", type: "title", verseText: verseText || "", verseRef: verseRef || "" },
        ...paginated,
        { header: "Questions?", body: "", type: "questions" },
        ...(talk?.references && typeof talk.references === "string" && talk.references.trim() !== "" ? (() => {
            const refParts = [
                talk.references,
                ...(Array.isArray(talk.referencesBodies) ? talk.referencesBodies : [])
            ].filter(Boolean);

            let accumulated = 0;
            return refParts.map((html, idx) => {
                const startNum = accumulated + 1; // continue numbering across parts
                const adjusted = setOlStart(html, startNum);
                const normalized = mergeAndNormalizeHTML([adjusted]);
                accumulated += countListItems(html);
                return {
                    header: idx === 0 ? "References" : "References (Continued)",
                    body: normalized,
                    type: "references",
                };
            });
        })() : [])
    ];
    return slides;
}

export function Slide({ sec, isActive, isTitle }) {
    const isDivider = sec?.type === 'divider';
    const isQuestions = sec?.type === 'questions';
    const isReferences = sec?.type === 'references';

    const imageUrl = (sec?.imageUrl || '').trim();
    const slideDisplayBody = sec?.body || '';
    const hasText = slideDisplayBody.replace(/<[^>]*>/g, '').trim() !== '';

    // paginateSections has already resolved which surface shows what, so the
    // image only reaches us when it belongs on the slide.
    const isGraphic = !!imageUrl && !hasText;
    const isHybrid = !!imageUrl && hasText;

    const activeClass = isActive ? ' active' : '';

    if (isTitle) {
        return (
            <div className={`slideshow-css slide title-slide${activeClass}`}>
                <div className="slide-content">
                    <h1>{sec.header}</h1>
                    {sec.verseText ? (
                        <div className="title-quote" role="note" aria-label="reference quote">
                            <blockquote className="title-quote__text">"{sec.verseText}"</blockquote>
                            {sec.verseRef && <div className="title-quote__ref">{sec.verseRef}</div>}
                        </div>
                    ) : (
                        <div className="title-sub">SOLE MINISTRIES</div>
                    )}
                </div>
                <Watermark />
                <Copyright />
            </div>
        );
    }

    if (isDivider) {
        return (
            <div className={`slideshow-css slide divider-slide${activeClass}`}>
                <div className="slide-content">
                    <h1 className="divider-heading">{sec.header}</h1>
                </div>
                <Watermark />
                <Copyright />
            </div>
        );
    }

    if (isQuestions) {
        return (
            <div className={`slideshow-css slide title-slide questions-slide${activeClass}`}>
                <div className="slide-content">
                    <h1>{sec.header}</h1>
                </div>
                <Watermark />
                <Copyright />
            </div>
        );
    }

    if (isReferences) {
        return (
            <div className={`slideshow-css slide${activeClass}`}>
                <div className="slide-content">
                    <h2>{sec.header}</h2>
                    <div className="slide-body" dangerouslySetInnerHTML={{ __html: sec.body }} />
                </div>
                <Watermark />
                <Copyright />
            </div>
        );
    }

    if (isGraphic && imageUrl) {
        return (
            <div className={`slideshow-css slide section-graphic${activeClass}`}>
                <div className="slide-content">
                    <h2>{sec.header}</h2>
                    <figure className="slide-graphic-figure">
                        <img
                            src={imageUrl}
                            alt={sec?.imageAlt || sec?.header || 'Slide image'}
                            className="slide-graphic-img"
                            loading="lazy"
                        />
                        {sec?.imageCaption && (
                            <figcaption className="slide-graphic-caption">{sec.imageCaption}</figcaption>
                        )}
                    </figure>
                </div>
                <Watermark />
                <Copyright />
            </div>
        );
    }

    if (isHybrid && imageUrl) {
        return (
            <div className={`slideshow-css slide section-hybrid${activeClass}`}>
                <div className="slide-content">
                    <h2>{sec.header}</h2>
                    <div className="slide-hybrid-grid">
                        <div className="slide-hybrid-left">
                            <div className="slide-body" dangerouslySetInnerHTML={{ __html: slideDisplayBody }} />
                        </div>
                        <div className="slide-hybrid-right">
                            <figure className="slide-hybrid-figure">
                                <img
                                    src={imageUrl}
                                    alt={sec?.imageAlt || sec?.header || 'Slide image'}
                                    className="slide-hybrid-img"
                                    loading="lazy"
                                />
                                {sec?.imageCaption && (
                                    <figcaption className="slide-hybrid-caption">{sec.imageCaption}</figcaption>
                                )}
                            </figure>
                        </div>
                    </div>
                </div>
                <Watermark />
                <Copyright />
            </div>
        );
    }

    return (
        <div className={`slideshow-css slide${activeClass}`}>
            <div className="slide-content">
                <h2>{sec.header}</h2>
                <div className="slide-body" dangerouslySetInnerHTML={{ __html: sec.body }} />
            </div>
            <Watermark />
            <Copyright />
        </div>
    );
}

export default function SlideshowPage() {
    const containerRef = useRef(null);  // fullscreen target
    const touchStart = useRef({ x: 0, y: 0, t: 0, moved: false });
    const justSwiped = useRef(false);

    const toggleFullScreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else if (containerRef.current) {
            containerRef.current.requestFullscreen();
        }
    };


    const { talkId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [talk, setTalk] = useState(null);
    // Resume on the slide we were on: the URL (?slide=) wins so a link can point
    // at a specific slide, otherwise the last position saved for this talk.
    const [current, setCurrent] = useState(() => {
        const fromUrl = parseInt(searchParams.get("slide"), 10);
        if (Number.isFinite(fromUrl) && fromUrl >= 0) return fromUrl;
        try {
            const stored = parseInt(localStorage.getItem(`slideshow:${talkId}:slide`), 10);
            if (Number.isFinite(stored) && stored >= 0) return stored;
        } catch {
            // localStorage unavailable — start at the beginning
        }
        return 0;
    });
    const [isFull, setIsFull] = useState(false);

    // listen for fullscreen toggle
    useEffect(() => {
        const onChange = () => {
            setIsFull(document.fullscreenElement === containerRef.current);
        };
        document.addEventListener("fullscreenchange", onChange);
        return () => document.removeEventListener("fullscreenchange", onChange);
    }, []);

    // Preload watermark image for smoother rendering
    useEffect(() => {
        const img = new Image();
        img.src = WATERMARK_SRC;
    }, []);

    useEffect(() => {
        if (!talkId) return;
        async function fetchTalk() {
            const snap = await getDoc(doc(db, "talks", talkId));
            if (snap.exists()) {
                setTalk(snap.data());
            }
        }
        fetchTalk();
    }, [talkId]);

    const { title } = talk || { title: "" };
    const slides = buildSlides(talk);
    const total = slides.length;
    const progressPct = ((current + 1) / total) * 100;

    // Once the talk (and its real slide count) has loaded, keep `current` in range.
    useEffect(() => {
        if (!talk) return;
        setCurrent((c) => Math.min(Math.max(0, c), total - 1));
    }, [talk, total]);

    // Persist the current slide to the URL (?slide=) and localStorage so a
    // refresh — or reopening this talk later — resumes where we left off.
    useEffect(() => {
        if (!talkId) return;
        try {
            localStorage.setItem(`slideshow:${talkId}:slide`, String(current));
        } catch {
            // ignore
        }
        setSearchParams(
            (prev) => {
                const p = new URLSearchParams(prev);
                p.set("slide", String(current));
                return p;
            },
            { replace: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current, talkId]);

    // ---- Remote control (phone) sync ----
    // A "remote session" is a small Firestore doc keyed by a short code. The
    // presenter and the phone page both read/write its `current` field, so
    // navigation flows in either direction. `remoteEcho` tracks the last value
    // this tab either sent or received, so applying a remote update doesn't
    // bounce straight back out as an outgoing write.
    const [remoteOpen, setRemoteOpen] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const remoteEcho = useRef(null);

    const startRemoteSession = useCallback(async () => {
        if (sessionId) return sessionId;
        const code = generateSessionCode();
        try {
            await setDoc(doc(db, "remoteSessions", code), {
                talkId,
                current,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            remoteEcho.current = current;
            setSessionId(code);
            return code;
        } catch (e) {
            console.error("Failed to start remote session", e);
            return null;
        }
    }, [sessionId, talkId, current]);

    const openRemote = useCallback(() => {
        setRemoteOpen(true);
        startRemoteSession();
    }, [startRemoteSession]);

    // Listen for the phone changing the slide.
    useEffect(() => {
        if (!sessionId) return undefined;
        const unsub = onSnapshot(doc(db, "remoteSessions", sessionId), (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();
            if (typeof data.current === "number" && data.current !== remoteEcho.current) {
                remoteEcho.current = data.current;
                setCurrent(data.current);
            }
        });
        return () => unsub();
    }, [sessionId]);

    // Push our own navigation (keyboard, swipe, buttons) out to the phone.
    useEffect(() => {
        if (!sessionId) return;
        if (current === remoteEcho.current) return;
        remoteEcho.current = current;
        updateDoc(doc(db, "remoteSessions", sessionId), {
            current,
            updatedAt: serverTimestamp(),
        }).catch((e) => console.error("Failed to sync remote session", e));
    }, [current, sessionId]);

    const prev = useCallback(
        () => setCurrent(c => (c - 1 + total) % total),
        [total]
    );
    const next = useCallback(
        () => setCurrent(c => (c + 1) % total),
        [total]
    );
    const handleTap = useCallback((e) => {
        if (justSwiped.current) return;
        if (!isFull) return;
        // Avoid advancing when clicking UI controls or links
        const target = e.target;
        if (target.closest && (target.closest('.slideshow-controls') || target.closest('a') || target.closest('button'))) return;
        next();
    }, [isFull, next]);

    const onTouchStart = useCallback((e) => {
        const t = e.touches && e.touches[0];
        if (!t) return;
        touchStart.current = { x: t.clientX, y: t.clientY, t: Date.now(), moved: false };
    }, []);

    const onTouchMove = useCallback((e) => {
        const t = e.touches && e.touches[0];
        if (!t) return;
        const dx = Math.abs(t.clientX - touchStart.current.x);
        const dy = Math.abs(t.clientY - touchStart.current.y);
        if (dx > 8 || dy > 8) touchStart.current.moved = true;
    }, []);

    const onTouchEnd = useCallback((e) => {
        const t = e.changedTouches && e.changedTouches[0];
        if (!t) return;
        const dx = t.clientX - touchStart.current.x;
        const dy = t.clientY - touchStart.current.y;
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);
        const dt = Date.now() - touchStart.current.t;

        // Consider it a horizontal swipe if distance & dominance thresholds are met
        const DIST = 50;        // pixels
        const DOMINANCE = 1.5;  // horizontal vs vertical
        const TIME = 800;       // ms (optional speed check)

        if (touchStart.current.moved && adx > DIST && adx > DOMINANCE * ady && dt < TIME) {
            if (dx < 0) {
                next();   // swipe left -> next
            } else {
                prev();   // swipe right -> previous
            }
            // prevent the subsequent click from triggering handleTap
            justSwiped.current = true;
            setTimeout(() => { justSwiped.current = false; }, 350);
        }
    }, [next, prev]);

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === "ArrowLeft" || e.key === "Backspace") {
                prev();
            } else if (e.key === "ArrowRight" || e.key === " ") {
                next();
            } else if (e.key === "Escape" && document.fullscreenElement) {
                document.exitFullscreen();
            } else if (typeof e.key === "string" && e.key.toLowerCase() === "f") {
                e.preventDefault();
                toggleFullScreen();
            }
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [prev, next]);

    if (!talkId) {
        return <p className="slideshow-css">Error: No talk ID provided.</p>;
    }

    if (!talk) {
        return <p className="slideshow-css">Loading slideshow...</p>;
    }

    return (
        <div className="slideshow-css talk-css donate-css">
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <Navbar />
            <div
                ref={containerRef}
                className={`slideshow-page ${isFull ? "fullscreen-mode" : ""}`}
                onDoubleClick={toggleFullScreen}
            >
                <header className="slideshow-hero">
                    <div className="slideshow-hero-content">
                        <h1>{title}</h1>
                    </div>
                    <div className="slideshow-progress">
                        <div
                            className="slideshow-progress-bar"
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                    <div className="slideshow-controls">
                        <Link to={`/talk/${talkId}`} className="slide-btn">
                            View Notes
                        </Link>
                        <button onClick={toggleFullScreen} className="slide-btn">
                            Fullscreen
                        </button>
                        <button onClick={openRemote} className="slide-btn">
                            Remote Control
                        </button>
                        <span className="slide-status">{`${current + 1} / ${total}`}</span>
                        <button onClick={prev} className="slide-btn slide-prev">
                            Prev
                        </button>
                        <button onClick={next} className="slide-btn slide-next">
                            Next
                        </button>
                    </div>
                </header>

                <div
                    className="slideshow-outer"
                    onClick={handleTap}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    <div className="slide-scaler">
                        {slides.map((sec, idx) => (
                            <Slide
                                key={idx}
                                sec={sec}
                                isActive={idx === current}
                                isTitle={idx === 0}
                            />
                        ))}
                    </div>
                </div>
                {isFull && (
                    <div
                        className="fs-progress"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(progressPct)}
                        aria-label="Slide progress"
                    >
                        <div
                            className="fs-progress__bar"
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                )}

                {remoteOpen && (
                    <div className="remote-modal-backdrop" onClick={() => setRemoteOpen(false)}>
                        <div className="remote-modal" onClick={(e) => e.stopPropagation()}>
                            <button
                                className="remote-modal-close"
                                onClick={() => setRemoteOpen(false)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                            <h2>Remote Control</h2>
                            <p>Scan this with your phone to advance slides from anywhere in the room.</p>
                            {sessionId ? (
                                <>
                                    <div className="remote-qr-wrap">
                                        <QRCodeSVG
                                            value={`${window.location.origin}/remote/${sessionId}`}
                                            size={220}
                                            bgColor="#fdf9f1"
                                            fgColor="#0f3650"
                                            level="M"
                                        />
                                    </div>
                                    <div className="remote-code">
                                        Code: <strong>{sessionId}</strong>
                                    </div>
                                </>
                            ) : (
                                <p>Starting session…</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
